import moment from "moment";
import type { CSSProperties } from "react";
import { useIntl } from "react-intl";
import Chip from "../../../../../../components/support/components/Chip";
import { convertMinutesToHours } from "../../../../../../utils/dateUtils";
import { subTopicSupportIds, typeSubjectOccorenceIds, typesOcorrenceStatus, typesOcorrenceStatusIds } from "../../../../../../components/support/constants";
import { IOccurrence } from "../../../../../../components/support/types";
import SupportItemCounts from "./SupportItemCounts";

interface Props {
  data: IOccurrence;
  onClick: () => void;
}

const statusChipStyle: Record<string, { bg: string; color: string; border: string }> = {
  OPEN: { bg: "#F0F9FF", color: "#026AA2", border: "#B9E6FE" },
  IN_ANALYSIS: { bg: "#F0F9FF", color: "#026AA2", border: "#B9E6FE" },
  ANSWERED: { bg: "#FFFAEB", color: "#B54708", border: "#FEDF89" },
  FINISHED: { bg: "#ECFDF3", color: "#027A48", border: "#ABEFC6" },
};

const pillStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 22,
  borderRadius: 16,
  border: "1px solid",
  padding: "0 14px",
  whiteSpace: "nowrap",
};

const blockStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  flex: "0 0 auto",
  paddingRight: 24,
  borderRight: "1px solid #E4E7EC",
};

const SupportItem = ({ data, onClick }: Props) => {
  const intl = useIntl();
  const statusStyle = statusChipStyle[data.status] ?? statusChipStyle.OPEN;
  const isFinished = data.status === "FINISHED";
  const subjectId = typeSubjectOccorenceIds[data.subject?.toUpperCase() as keyof typeof typeSubjectOccorenceIds];
  const subject = subjectId ? intl.formatMessage({ id: subjectId }) : data.subject || "-";
  const subtopicId = subTopicSupportIds[data.subtopic as keyof typeof subTopicSupportIds];
  const subtopic = subtopicId ? intl.formatMessage({ id: subtopicId }) : data.subtopic;
  const statusId = typesOcorrenceStatusIds[data.status as keyof typeof typesOcorrenceStatusIds];

  return (
    <div
      className="bg-white border rounded rounded-small cursor-pointer margin-bottom-10 flex-wrap flex-nowrap-sm"
      style={{ borderColor: "#E4E7EC", padding: "14px 16px", display: "flex", alignItems: "center", gap: 24, rowGap: 12 }}
      onClick={onClick}
    >
      <div style={blockStyle}>
        <span className="text-medium text-size-14">{data.tasOccurrenceNumber || "-"}</span>
      </div>

      <div className="text-size-12 text-normal" style={{ ...blockStyle, color: "#344054", maxWidth: 200 }}>
        <span>
          {subject}
          {subtopic && (
            <>
              <br />
              {subtopic}
            </>
          )}
        </span>
      </div>

      <div style={blockStyle}>
        <SupportItemCounts id={data.id} />
      </div>

      <div style={{ ...blockStyle, gap: 8, flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex" }}>
          <Chip type={data.supportType} />
        </span>
        <span style={{ ...pillStyle, background: statusStyle.bg, color: statusStyle.color, borderColor: statusStyle.border }}>
          <span className="text-size-12 text-medium">{statusId ? intl.formatMessage({ id: statusId }) : typesOcorrenceStatus[data.status]}</span>
        </span>
      </div>

      <div className="text-size-12 text-normal" style={{ ...blockStyle, color: "#667085" }}>
        {isFinished
          ? data.statusUpdatedAt
            ? intl.formatMessage({ id: "intl-msg:kanban.form.supportItem.finishedAt" }, { date: moment(data.statusUpdatedAt).format("DD/MM/YYYY HH:mm") })
            : intl.formatMessage({ id: "intl-msg:tas.occurrence.card.finished" })
          : data.timeInStatus
            ? intl.formatMessage({ id: "intl-msg:kanban.form.supportItem.timeInStep" }, { time: convertMinutesToHours(data.timeInStatus) })
            : "-"}
      </div>

      <div
        className="text-size-12 text-normal text-right"
        style={{ color: "#667085", flex: "0 1 auto", marginLeft: "auto", minWidth: 0, maxWidth: "100%" }}
      >
        <p style={{overflowWrap: "anywhere", wordBreak: "break-word", margin: 0}}>
          {data.customer || ""}
        </p>
        <p>{data.responsible || "-"}</p>
      </div>
    </div>
  );
};

export default SupportItem;
